import {
	Body,
	Button,
	FileUpload,
	FlexBox,
	FormControl,
	FormError,
	Headline,
	Input,
	InputMask,
	Message,
	Select,
} from '@Backlot-Cars/archie';
import { yupResolver } from '@hookform/resolvers/yup';
import DriversLicenseUploadField from 'components/DriversLicenseUploadField';
import {
	LICENSE_ACCEPT_TYPES_WITH_PDF,
	LICENSE_IMAGE_ACCEPT_TYPES,
} from 'components/DriversLicenseUploadField/fileTypes';
import { isRecaptchaVersion, recaptchaEnabled } from 'helpers/recaptcha';
import { auctionAccessIdValidator } from 'helpers/regex';
import { shouldShowAuctionAccessDealerId } from 'helpers/signUp/auctionAccessRules';
import { treatments as treats } from 'helpers/split/treatments';
import { Trans, useTranslation } from 'next-i18next';
import { useTreatments } from 'hooks/useTreatments';
import Router, { useRouter } from 'next/router';
import Recaptcha from 'partials/lp/inspectors/Recaptcha';
import RecaptchaV3 from 'partials/recaptcha/v3';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { SIGNUP_PATH_IDS } from 'constants/signUp';
import { BUSINESS_TYPES, YES_OR_NO_OPTIONS } from './data';
import { isExistingAccountError } from '../../helpers/signUp/errorUtils';
import SignUpFormActions from './FormActions';
import SignUpHeader from './header';
import {
	getFileName,
	invalidAfcOktaCredentials,
	isAfcOktaUser,
	isFastPath,
	isAffiliatePartner,
	parseUrl,
	hasPathId,
} from './helpers';
import SignUpPolicies from './policies';
import RadioButtonFormField from './RadioButtonFormField';
import { AddressAutocomplete } from 'components/AddressAutocomplete';
import { Container, LegalBusinessNameContainer } from './styles';
import STATES from 'helpers/states';

const enableDriversLicense = (query) => {
	query.driversLicense = 'true';
	Router.push({ pathname: '/sign_up', query }, undefined, { scroll: false });
};

const enableDealerLicense = (query) => {
	query.dealerLicense = 'true';
	Router.push({ pathname: '/sign_up', query }, undefined, { scroll: false });
};

const AuctionAccessDealerId = ({
	register,
	errors,
	values,
	query,
	t,
	isAfcFastPathSignup,
	isFastPathSignup,
	isAffiliatePartnerSignup,
	showDriversLicense,
	showDealerLicense,
	isAuctionAccessOnboardingFlagEnabled,
}) => {
	const isAfcOrAffiliatePartnerSignup = isAfcFastPathSignup || isAffiliatePartnerSignup;
	const shouldShowStandardDealerId =
		!isAfcOrAffiliatePartnerSignup && (!isAuctionAccessOnboardingFlagEnabled || isFastPathSignup);

	if (!isAfcOrAffiliatePartnerSignup && !shouldShowStandardDealerId) return null;

	return (
		<FlexBox flexDirection="column" gap="sm">
			{shouldShowStandardDealerId && (
				<FormControl label={t('signup:steps.step0.auctionAccessDealerId')} formErrors={errors}>
					<Input id="auctionAccessDealerId" maxLength="50" {...register('auctionAccessDealerId')} />
				</FormControl>
			)}
			{isAfcOrAffiliatePartnerSignup && (
				<>
					<FormControl
						label={
							isAfcFastPathSignup ?
								t('signup:steps.step0.auctionAccessDealerIdAfcFastPath')
							:	t('signup:steps.step0.auctionAccessDealerId')
						}
						formErrors={errors}
						required={isAfcFastPathSignup}
						hideErrorMessage={showDriversLicense}
					>
						<Input id="auctionAccessDealerId" maxLength="50" {...register('auctionAccessDealerId')} />
					</FormControl>
					{showDriversLicense &&
						errors?.auctionAccessDealerId &&
						!auctionAccessIdValidator(values.auctionAccessDealerId) && (
							<FormError text={t('signup:steps.step0.invalidAuctionAccessDealerId')} />
						)}
					<FlexBox gap="sm" flexDirection="column">
						<Body>
							{isAffiliatePartnerSignup ?
								t('signup:steps.step0.driversLicenseRecommendationAffiliatePartner')
							:	t('signup:steps.step0.driversLicenseRecommendation')}
						</Body>
						{(() => {
							const showDriversLicenseButton = !showDriversLicense;
							const showDealerLicenseButton = isAffiliatePartnerSignup && !showDealerLicense;
							const shouldShowButtons =
								isAffiliatePartnerSignup ?
									showDriversLicenseButton || showDealerLicenseButton
								:	showDriversLicenseButton;

							return (
								shouldShowButtons && (
									<FlexBox gap="sm">
										{showDriversLicenseButton && (
											<Button
												variant="tertiary"
												size="smaller"
												onClick={() => enableDriversLicense(query)}
											>
												{t('signup:steps.step0.uploadDriversLicense')}
											</Button>
										)}
										{showDealerLicenseButton && (
											<Button
												variant="tertiary"
												size="smaller"
												onClick={() => enableDealerLicense(query)}
											>
												Upload dealer&apos;s license
											</Button>
										)}
									</FlexBox>
								)
							);
						})()}
					</FlexBox>
				</>
			)}
		</FlexBox>
	);
};

export const DriversLicense = ({ register, errors, resetField, watch }) => {
	const driverLicenseFile = watch('driverLicenseFile');
	const driverLicenseUrl = watch('driverLicenseUrl');

	return (
		<DriversLicenseUploadField
			register={register}
			name="driverLicenseFile"
			errors={errors}
			lastFileName={getFileName(driverLicenseFile)}
			lastFileUrl={driverLicenseUrl || parseUrl(driverLicenseFile)}
			handleRemove={() => resetField('driverLicenseFile')}
			description="Upload or take a photo of the user's driver's license."
			fileTypesText="File types: .jpeg, .jpg, .png"
			accept={LICENSE_IMAGE_ACCEPT_TYPES}
		/>
	);
};

export const DealerLicense = ({ register, errors, setValue, watch, t }) => {
	const dealerLicenseFile = watch('dealerLicenseFile');
	const dealerLicenseUrl = watch('dealerLicenseUrl');

	const unsetValue = (field) => {
		setValue(field, null);
	};

	return (
		<FormControl
			label={t('signup:steps.step3.dealerLicenseFile')}
			tooltipMessage={t('signup:steps.step3.dealerLicenseFileInformation')}
			tooltipPosition="bottom"
			formErrors={errors}
		>
			<FileUpload
				accept={LICENSE_ACCEPT_TYPES_WITH_PDF}
				lastFileName={getFileName(dealerLicenseFile)}
				lastFileUrl={dealerLicenseUrl || parseUrl(dealerLicenseFile)}
				handleRemove={() => unsetValue('dealerLicenseFile')}
				fullWidth
				{...register('dealerLicenseFile')}
			/>
		</FormControl>
	);
};

const StepZero = (props) => {
	const { t } = useTranslation(['common', 'signup']);
	const { onSubmit, initialValues, signUpPolicies, treatments } = props;
	const auctionAccessOnboardingEnabled = useTreatments(treats.auction_access_onboarding_enabled);
	const { query } = useRouter();
	const isFastPathSignup = isFastPath(query) || hasPathId(SIGNUP_PATH_IDS.privateLabelPath, query);
	const isAfcFastPathSignup = isAfcOktaUser(query);
	const isAffiliatePartnerSignup = isAffiliatePartner(query);
	const isOneSingleStepSignUp = isAfcFastPathSignup || isFastPathSignup || isAffiliatePartnerSignup;
	const isOrganicSignup = !isOneSingleStepSignUp;
	const isRecaptchaV2 = isRecaptchaVersion(2, query);
	const showDriversLicense = query.driversLicense === 'true';
	const showDealerLicense = query.dealerLicense === 'true';
	const showAuctionAccessDealerId = shouldShowAuctionAccessDealerId({
		isOrganicSignup,
		isAfcFastPath: isAfcFastPathSignup,
		isFastPath: isFastPathSignup,
		isAffiliatePartner: isAffiliatePartnerSignup,
	});
	const showUSLicense =
		treatments.fall_2024_dealer_radio_removal !== 'on' && !isFastPathSignup && !isAffiliatePartnerSignup;
	if (!showUSLicense) {
		initialValues.hasUSLicense = 'yes';
	}
	const {
		register,
		handleSubmit,
		setValue,
		getValues,
		resetField,
		setError,
		setFocus,
		watch,
		formState: { errors, isSubmitting },
	} = useForm({
		defaultValues: {
			...initialValues,
		},
		resolver: yupResolver(props.validationSchema),
	});

	const values = getValues();
	const auctionAccessDealerIdField = showAuctionAccessDealerId && (
		<AuctionAccessDealerId
			register={register}
			errors={errors}
			values={values}
			query={query}
			t={t}
			isAfcFastPathSignup={isAfcFastPathSignup}
			isFastPathSignup={isFastPathSignup}
			isAffiliatePartnerSignup={isAffiliatePartnerSignup}
			showDriversLicense={showDriversLicense}
			showDealerLicense={showDealerLicense}
			isAuctionAccessOnboardingFlagEnabled={auctionAccessOnboardingEnabled}
		/>
	);

	const treatmentsObj = useTreatments();
	const addressAutocompleteOn = treatmentsObj.address_autocomplete_signup === 'on';

	const addressAutocompleteLabel = t('signup:steps.step2.dealershipAddressAutocomplete');
	const addressAutocompleteProps = {
		register,
		errors,
		setValue,
		label: addressAutocompleteLabel,
		placeholder: addressAutocompleteLabel,
	};

	const renderAddressField = () => {
		if (addressAutocompleteOn) {
			return <AddressAutocomplete {...addressAutocompleteProps} />;
		}
		return (
			<>
				<FormControl label={t('signup:steps.step2.dealershipAddress')} required formErrors={errors}>
					<Input id="dealershipAddress" maxLength="255" {...register('dealershipAddress')} />
				</FormControl>
				<FormControl label={t('signup:steps.step2.dealershipAddress2')} formErrors={errors}>
					<Input id="dealershipAddress2" maxLength="255" {...register('dealershipAddress2')} />
				</FormControl>
				<Container>
					<FormControl label={t('signup:steps.step2.city')} required formErrors={errors}>
						<Input id="city" maxLength="255" {...register('city')} />
					</FormControl>
					<FormControl label={t('signup:steps.step2.state')} required formErrors={errors}>
						<Select
							id="state"
							maxItems="5"
							data-testid="state"
							options={Object.keys(STATES).map((key) => ({ value: key, text: key }))}
							{...register('state')}
						/>
					</FormControl>
					<FormControl label={t('signup:steps.step2.zip')} required formErrors={errors}>
						<Input id="zip" maxLength="5" {...register('zip')} />
					</FormControl>
				</Container>
			</>
		);
	};

	useEffect(() => {
		if (isExistingAccountError(props.error)) {
			setError('email', {
				type: 'manual',
				message: props.error,
			});

			setFocus('email');
		}
	}, [props.error, setError, setFocus]);

	return (
		<>
			{isAfcFastPathSignup && (
				<Body color="success" bold>
					{t('signup:steps.step0.afcOktaCredentials.valid')}
				</Body>
			)}

			{invalidAfcOktaCredentials(query) && (
				<Body color="alert" bold>
					{t('signup:steps.step0.afcOktaCredentials.invalid')}
				</Body>
			)}

			{isFastPathSignup && (
				<Message iconName="info" color="brand" iconColor="brand" borderColor="brand">
					<Body>{t('signup:steps.step0.fastPathOnlyForFranchise')} </Body>
				</Message>
			)}

			{auctionAccessOnboardingEnabled ?
				<Headline size="H4">{t('signup:steps.step0.title')}</Headline>
			:	<SignUpHeader title={t('signup:steps.step0.title')} subTitle={t('signup:steps.step0.subTitle')} />}

			<Container alignItems="flex-start">
				<FormControl label={t('signup:steps.step1.firstName')} required formErrors={errors}>
					<Input id="firstName" maxLength="255" {...register('firstName')} />
				</FormControl>
				<FormControl label={t('signup:steps.step1.lastName')} required formErrors={errors}>
					<Input id="lastName" maxLength="255" {...register('lastName')} />
				</FormControl>
			</Container>

			{/* Please don't remove Legal Business Name. It's a honey trap for bots. */}
			<LegalBusinessNameContainer>
				<FormControl label="Legal Business Name" required>
					<Input id="legalBusinessName" maxLength="255" {...register('legalBusinessName')} />
				</FormControl>
			</LegalBusinessNameContainer>

			<FormControl label="Email" required formErrors={errors}>
				<Input
					id="email"
					maxLength="255"
					disabled={isAfcFastPathSignup && initialValues.email !== ''}
					{...register('email')}
				/>
			</FormControl>

			<FormControl label={t('signup:steps.step1.mobilePhone')} required formErrors={errors}>
				<InputMask
					id="mobilePhone"
					prefix="+1"
					mask="(999) 999-9999"
					defaultValue={initialValues.mobilePhone}
					{...register('mobilePhone')}
				/>
			</FormControl>

			<FormControl
				label={t('signup:steps.step2.dealershipName')}
				required
				formErrors={errors}
				tooltipMessage={!showUSLicense ? t('signup:steps.step2.dealershipNameTooltip') : undefined}
				tooltipPosition="bottom"
			>
				<Input id="dealershipName" maxLength="70" {...register('dealershipName')} />
			</FormControl>

			{isOrganicSignup && auctionAccessDealerIdField}

			{isAfcFastPathSignup && renderAddressField()}
			{showUSLicense && (
				<FormControl label={t('signup:steps.step0.hasUSLicense')} required formErrors={errors}>
					<RadioButtonFormField
						id="hasUSLicense"
						options={YES_OR_NO_OPTIONS}
						defaultValue={initialValues.hasUSLicense}
						direction="row"
						itemPadding="0 50px 0 0"
						itemAlignment="center"
						labelGap="8px"
						register={register}
						setValue={setValue}
						errors={errors}
					/>
				</FormControl>
			)}

			{(isFastPathSignup || isAffiliatePartnerSignup) && renderAddressField()}

			<FormControl label={t('signup:steps.step0.businessType')} required formErrors={errors}>
				<Select
					id="businessType"
					data-testid="businessType"
					placeholder={t('common:selectOne')}
					options={BUSINESS_TYPES}
					defaultValue={parseInt(initialValues.businessType)}
					{...register('businessType')}
				/>
			</FormControl>

			{!isOrganicSignup && auctionAccessDealerIdField}

			{(isAfcFastPathSignup || isAffiliatePartnerSignup) && showDriversLicense && (
				<DriversLicense register={register} errors={errors} resetField={resetField} watch={watch} />
			)}

			{isAffiliatePartnerSignup && showDealerLicense && (
				<DealerLicense register={register} errors={errors} setValue={setValue} watch={watch} t={t} />
			)}

			{isAfcFastPathSignup && (
				<FormControl label={t('signup:steps.step0.stateTaxId')} required formErrors={errors}>
					<Input id="stateTaxId" maxLength="255" {...register('stateTaxId')} />
				</FormControl>
			)}

			{recaptchaEnabled && isRecaptchaV2 ?
				<FormControl required formErrors={errors}>
					<Recaptcha id="recaptcha" setValue={setValue} {...register('recaptcha')} />
				</FormControl>
			:	<RecaptchaV3 id="recaptcha-v3" />}

			{signUpPolicies?.length > 0 && (
				<SignUpPolicies
					policies={signUpPolicies}
					policyValues={[values.privacy_policy, values.email_policy, values.sms_terms_and_conditions]}
					smsPolicyCopy={t('signup:steps.step0.smsPolicy')}
					smsLinkCopy={t('signup:steps.step0.smsLink')}
					errors={errors}
					register={register}
					setValue={setValue}
					t={t}
				/>
			)}

			<SignUpFormActions
				handleSubmit={handleSubmit((values) => onSubmit(values))}
				isSubmitting={isSubmitting}
				backComponent={
					<Body size="normal">
						<Trans
							i18nKey="signup:actions.signIn"
							t={t}
							components={[<Button variant="link" key="signInUrl" href={props.signInUrl} />]}
						/>
					</Body>
				}
				nextCopy={
					isAffiliatePartnerSignup ? t('signup:actions.claimAccount')
					: isOneSingleStepSignUp ?
						t('signup:actions.submit')
					:	t('signup:actions.next')
				}
				{...props}
			/>

			{!isExistingAccountError(props.error) && <FormError text={props.error} />}
		</>
	);
};

export default StepZero;
